import React, { Fragment, useState } from 'react';
import moment from 'moment';
import {
	Menu,
	MenuButton,
	MenuItem,
	MenuItems,
	Transition,
} from '@headlessui/react';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

import Utilities from '@util/Utilities';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import Spinner from '@app/component/static/Spinner';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useSiteNotificationTools } from '@app/site/secured/rateshopper/rates/provider/useSiteNotificationTools';
import ParityFilterInput from '@app/model/parity/input/ParityFilterInput';
import SavedMarketStateFilterFilter from '@app/model/market_state/_saved_filters/SavedMarketStateFilterFilter';
import ParityExportReportInput from '@app/model/parity/_export/input/ParityExportReportInput';
import ParityExportService from '@app/model/parity/_export/service/ParityExportService';
import { ParityExportReportPeriod } from '@app/model/parity/_export/ParityExportReport';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import CalendarMonth from '@app/state/calendar/CalendarMonth';
import Refunding from '@app/model/market_state/Refunding';

type ExportOption = {
	period: ParityExportReportPeriod;
	monthCount: number;
};

const EXPORT_OPTIONS: ExportOption[] = [
	{ period: ParityExportReportPeriod.CURRENT_MONTH, monthCount: 1 },
	{ period: ParityExportReportPeriod.MONTHS_3, monthCount: 3 },
	{ period: ParityExportReportPeriod.MONTHS_6, monthCount: 6 },
	{ period: ParityExportReportPeriod.MONTHS_12, monthCount: 12 },
];

const DEFAULT_PARITY_EXPORT_GUESTS = 2;

const createSafeFileNamePart = (value: string): string => value
	.trim()
	.replace(/\s+/g, '_')
	.replace(/[^a-zA-Z0-9_-]/g, '')
	.toLowerCase();

const toExportFilter = (filter: ParityFilterInput): SavedMarketStateFilterFilter => ({
	roomTypes: [],
	region: filter.region,
	ratesType: filter.ratesType,
	guests: DEFAULT_PARITY_EXPORT_GUESTS,
	nights: filter.nights,
	airportCode: filter.airportCode,
	meal: undefined,
	refunding: Refunding.NONE,
});

const buildFilterFileNamePart = (filter: SavedMarketStateFilterFilter): string => {
	const parts: string[] = [];

	if (filter.region !== undefined) parts.push(String(filter.region));
	if (filter.ratesType !== undefined) parts.push(String(filter.ratesType));
	if (typeof filter.guests === 'number') parts.push(`guests${filter.guests}`);
	if (typeof filter.nights === 'number') parts.push(`los${filter.nights}`);
	if (filter.meal !== undefined) parts.push(String(filter.meal));
	if (filter.refunding !== undefined) parts.push(String(filter.refunding));
	if (filter.airportCode !== undefined) parts.push(String(filter.airportCode));
	if ((filter.roomTypes?.length ?? 0) > 0) parts.push(filter.roomTypes.map((rt: HotelRoomMappingType) => String(rt)).join('_'));

	return createSafeFileNamePart(parts.length > 0 ? parts.join('_') : 'any');
};

const buildExportName = (
	companyHotel: CompanyHotel,
	calendarMonth: CalendarMonth,
	period: ParityExportReportPeriod,
	filter: SavedMarketStateFilterFilter,
): string => {
	const hotelName = companyHotel.mainHotel.name ?? 'hotel';
	const shortHotelNamePart = createSafeFileNamePart(hotelName).substring(0, 10);
	const shortHotelName = shortHotelNamePart.length > 0 ? shortHotelNamePart : 'hotel';
	const createdAt = Utilities.formatDate(new Date(), 'DD-MMM-yyyy');
	const filterName = buildFilterFileNamePart(filter);

	return `${shortHotelName}_${calendarMonth.index.month}_${createdAt}_${period.toLowerCase()}_${filterName}`.toLowerCase();
};

const getSelectedMonthStart = (calendarMonth: CalendarMonth): moment.Moment => moment(new Date(
	calendarMonth.index.year,
	Utilities.getMonthOrdinal(calendarMonth.index.month),
	1,
));

const formatSelectedMonthRangeLabel = (calendarMonth: CalendarMonth, monthCount: number): string => {
	const start = getSelectedMonthStart(calendarMonth);
	const end = start.clone().add(monthCount - 1, 'months');

	if (monthCount === 1) return start.format('MMMM YYYY');

	if (start.year() === end.year()) {
		return `${start.format('MMM')} - ${end.format('MMM YYYY')}`;
	}

	return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
};

const getSelectedMonthDateRange = (
	calendarMonth: CalendarMonth,
	monthCount: number,
): { fromDate: string; toDate: string } => {
	const start = getSelectedMonthStart(calendarMonth);
	const end = start.clone().add(monthCount - 1, 'months').endOf('month');

	return {
		fromDate: start.format('YYYY-MM-DD'),
		toDate: end.format('YYYY-MM-DD'),
	};
};

const ParityExportToExcelSelectorFragment: React.FunctionComponent<{
	companyHotel: CompanyHotel;
	calendarMonth: CalendarMonth;
	filter: ParityFilterInput;
}> = ({ companyHotel, calendarMonth, filter }) => {
	const toaster = useToaster();
	const siteNotificationTools = useSiteNotificationTools();
	const [isExporting, setIsExporting] = useState(false);
	const exportFilter = toExportFilter(filter);
	const exportOptions = EXPORT_OPTIONS.map(option => ({
		...option,
		label: formatSelectedMonthRangeLabel(calendarMonth, option.monthCount),
	}));

	const saveExport = async (option: ExportOption): Promise<void> => {
		setIsExporting(true);

		try {
			const dateRange = getSelectedMonthDateRange(calendarMonth, option.monthCount);
			const input: ParityExportReportInput = {
				companyHotelId: companyHotel.id,
				period: option.period,
				...dateRange,
				name: buildExportName(companyHotel, calendarMonth, option.period, exportFilter),
				filter: exportFilter,
			};

			await ParityExportService.saveParityExportReport(input);
			toaster.success('Downloads', 'Excel processing has been started.');
			siteNotificationTools.refetch();
		} catch (error) {
			console.error('Error saving parity export:', error);
			toaster.error('Downloads', 'Error saving export. Please try again later!');
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<Menu as="div" className="relative">
			<MenuButton
				type="button"
				className="btn btn-white font-normal py-1.5 flex items-center cursor-pointer"
				disabled={isExporting}
			>
				{isExporting ? <Spinner /> : <DocumentArrowDownIcon className="-ml-1 mr-1 h-4 w-4" />}
				{isExporting ? ' Exporting...' : 'Export'}
				<ChevronDownIcon className="-mr-1 ml-1 h-5 w-5 text-blue-500" />
			</MenuButton>

			{!isExporting && (
				<Transition
					as={Fragment}
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
				>
					<MenuItems className="absolute right-0 z-10 mt-3 w-44 origin-top-right overflow-hidden rounded-md bg-white divide-y divide-gray-100 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
						<div className="px-4 py-3">
							<p className="text-sm font-semibold text-gray-900">Export (.xlsx)</p>
						</div>
						<div className="py-1">
							{exportOptions.map((option) => (
								<MenuItem key={option.period}>
									{({ focus }) => (
										<button
											type="button"
											onClick={() => saveExport(option)}
											className={Utilities.classNames(
												focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
												'block w-full px-4 py-2 text-xs text-left cursor-pointer',
											)}
										>
											{option.label}
										</button>
									)}
								</MenuItem>
							))}
						</div>
					</MenuItems>
				</Transition>
			)}
		</Menu>
	);
};

export default ParityExportToExcelSelectorFragment;
